import { Decimal, formatINR, formatINRCompact, amountInWords, type MoneyInput } from '@bruhat/shared';

export { formatINR, formatINRCompact, amountInWords, Decimal };
export type { MoneyInput };

/** Money always arrives from the API as a decimal string. */
export function money(value: MoneyInput): string {
  return formatINR(value ?? 0);
}

export function moneyCompact(value: MoneyInput): string {
  return formatINRCompact(value ?? 0);
}

export function isNegative(value: MoneyInput): boolean {
  return new Decimal(value ?? 0).isNegative();
}

export function signedClass(value: MoneyInput): string {
  const d = new Decimal(value ?? 0);
  if (d.isZero()) return 'text-ink-subtle dark:text-slate-500';
  return d.isNegative() ? 'text-negative' : 'text-ink dark:text-slate-100';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 2026-04-15 -> "15 Apr 2026" — the format Indian accountants read fastest. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const iso = typeof value === 'string' ? value : value.toISOString();
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return '—';
  return `${d} ${MONTHS[Number(m) - 1]} ${y}`;
}

export function formatDateShort(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const iso = typeof value === 'string' ? value : value.toISOString();
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${String(y).slice(2)}`;
}

export function formatMonth(value: string): string {
  const [y, m] = value.slice(0, 10).split('-');
  return `${MONTHS[Number(m) - 1]} ${String(y).slice(2)}`;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function formatNumber(value: MoneyInput, decimals = 2): string {
  return formatINR(value ?? 0, { decimals, symbol: false });
}

export function formatPercent(value: MoneyInput, decimals = 1): string {
  return `${new Decimal(value ?? 0).toDecimalPlaces(decimals).toString()}%`;
}

export function formatArea(value: MoneyInput): string {
  return `${formatINR(value ?? 0, { decimals: 0, symbol: false })} sqft`;
}

/** Turn SNAKE_CASE enums into readable labels. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
